import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import SignaturePad from "signature_pad";
import { X, ChevronLeft, ChevronRight, PenTool, RotateCcw, Check, Loader } from "lucide-react";
import { submitVendorSignatures, getEventRequiredDocument } from "../../../services/EventDocumentService";
import { getFunctions, httpsCallable } from "firebase/functions";
import { isDemoMode } from "../../../demo/isDemoMode";
import {
  createNotification,
  NOTIFICATION_TYPES,
} from "../../../services/PebbleNotificationService";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/**
 * DocumentSigningModal — vendor signs a document.
 * Props:
 *   eventId        — string
 *   document       — requiredDocument object (downloadURL, title, signatureFields, id)
 *   vendorId       — string
 *   vendorName     — string
 *   onComplete()   — called after all fields signed & PDF generated
 *   onClose()      — close without signing
 */
const DocumentSigningModal = ({ eventId, document, vendorId, vendorName, onComplete, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [signatures, setSignatures] = useState({}); // { [fieldId]: { type, dataURL } }
  const [activeField, setActiveField] = useState(null); // field object being signed
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const pageContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);

  const fields = document.signatureFields || [];
  const currentPageFields = fields.filter((f) => f.page === currentPage);
  const allRequiredSigned = fields.every((f) => !f.required || signatures[f.id]);

  // Set up signature pad when modal opens for a field
  useEffect(() => {
    if (!activeField || !canvasRef.current) return;

    const canvas = canvasRef.current;
    // Handle HiDPI screens
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255,255,255,0)",
      penColor: "#1a1a1a",
    });

    // Pre-fill if already signed
    if (signatures[activeField.id]?.dataURL) {
      pad.fromDataURL(signatures[activeField.id].dataURL);
    }

    signaturePadRef.current = pad;

    return () => {
      pad.off();
      signaturePadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField]);

  const onPageLoadSuccess = (page) => {
    if (pageContainerRef.current) {
      const containerWidth = pageContainerRef.current.clientWidth;
      const viewport = page.getViewport({ scale: 1 });
      setPageSize({
        width: containerWidth,
        height: (viewport.height / viewport.width) * containerWidth,
      });
    }
  };

  const clearPad = () => {
    signaturePadRef.current?.clear();
  };

  const confirmSignature = () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) return;
    const dataURL = signaturePadRef.current.toDataURL("image/png");
    setSignatures((prev) => ({
      ...prev,
      [activeField.id]: { type: activeField.type, dataURL },
    }));
    setActiveField(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      // 1. Write signatures to Firestore
      const result = await submitVendorSignatures(eventId, document.id, vendorId, vendorName, signatures);
      if (!result.success) throw new Error(result.error);

      // 2. Call Cloud Function to generate signed PDF (skip in demo mode)
      if (!isDemoMode) {
        const functions = getFunctions();
        const generateSignedPDF = httpsCallable(functions, "generateSignedPDF");
        await generateSignedPDF({ eventId, documentId: document.id, vendorId });
      }

      // 3. Notify the event organizer
      try {
        const docRecord = await getEventRequiredDocument(eventId, document.id);
        const organizerId = docRecord?.document?.organizerId;
        if (organizerId) {
          await createNotification({
            recipientId: organizerId,
            recipientType: 'eo',
            type: NOTIFICATION_TYPES.DOCUMENT_SIGNED,
            title: 'Document signed',
            message: `${vendorName} signed "${document.title}".`,
            eventId,
            documentId: document.id,
            linkPath: `/eo/events/${eventId}`,
          });
        }
      } catch (_) { /* non-blocking */ }

      onComplete();
    } catch (err) {
      console.error("Error submitting signatures:", err);
      setError("Failed to submit signatures. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldStatus = (field) => {
    if (signatures[field.id]) return "signed";
    if (activeField?.id === field.id) return "active";
    return "unsigned";
  };

  const fieldStatusStyles = {
    signed:   "border-green-500 bg-green-50/70",
    active:   "border-blue-500 bg-blue-50/70",
    unsigned: "border-pebble-red bg-red-50/50 animate-pulse",
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-grey-200 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-grey-900">Sign Document</h2>
          <p className="text-sm text-grey-500">{document.title}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-grey-500">
            {Object.keys(signatures).length}/{fields.length} fields signed
          </span>
          <button
            onClick={handleSubmit}
            disabled={!allRequiredSigned || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-pebble-red text-white rounded-lg hover:bg-red-700 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>
            ) : (
              <><Check className="w-4 h-4" /> Submit Signatures</>
            )}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-grey-100 rounded-lg transition">
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* PDF area */}
      <div className="flex-1 bg-grey-100 overflow-auto flex flex-col items-center p-6 gap-4">
        {numPages && numPages > 1 && (
          <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-2 shadow-sm">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 hover:bg-grey-100 rounded disabled:opacity-40 transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-grey-700">
              Page {currentPage} of {numPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
              disabled={currentPage === numPages}
              className="p-1 hover:bg-grey-100 rounded disabled:opacity-40 transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="relative shadow-xl bg-white" style={{ width: "100%", maxWidth: 780 }}>
          <div
            ref={pageContainerRef}
            className="relative"
            style={{ minHeight: pageSize.height || 200 }}
          >
            <Document
              file={document.downloadURL}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className="flex items-center justify-center py-20 text-grey-500 text-sm">Loading...</div>
              }
            >
              <Page
                pageNumber={currentPage}
                width={pageContainerRef.current?.clientWidth || 780}
                onLoadSuccess={onPageLoadSuccess}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>

            {/* Signature field overlays */}
            {pageSize.width > 0 &&
              currentPageFields.map((field) => {
                const status = getFieldStatus(field);
                const isSigned = status === "signed";
                return (
                  <div
                    key={field.id}
                    className={`absolute border-2 rounded cursor-pointer flex items-center justify-center transition-all ${fieldStatusStyles[status]}`}
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`,
                    }}
                    onClick={() => !isSigned && setActiveField(field)}
                    title={isSigned ? "Already signed" : `Click to sign (${field.label})`}
                  >
                    {isSigned ? (
                      <img
                        src={signatures[field.id].dataURL}
                        alt="Signature"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <PenTool className="w-4 h-4 text-pebble-red" />
                        <span className="text-xs font-medium text-pebble-red">{field.label}</span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Unsigned fields on other pages notice */}
        {fields.some((f) => f.page !== currentPage && !signatures[f.id]) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
            You have unsigned fields on other pages. Use the page navigation above to complete them.
          </div>
        )}
      </div>

      {/* Signature capture panel */}
      {activeField && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-5 border-b border-grey-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-grey-900">
                  {activeField.type === "initials" ? "Draw Your Initials" : "Draw Your Signature"}
                </h3>
                <p className="text-xs text-grey-500 mt-0.5">Use your mouse or finger to sign in the box below</p>
              </div>
              <button onClick={() => setActiveField(null)} className="p-1 hover:bg-grey-100 rounded-lg">
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>

            <div className="p-5">
              <div className="border-2 border-grey-300 rounded-xl overflow-hidden bg-white" style={{ height: 160 }}>
                <canvas ref={canvasRef} className="w-full h-full touch-none" />
              </div>
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={clearPad}
                  className="flex items-center gap-1.5 text-sm text-grey-500 hover:text-grey-700 transition"
                >
                  <RotateCcw className="w-4 h-4" />
                  Clear
                </button>
                <p className="text-xs text-grey-400">
                  By signing, you agree this is a legally binding electronic signature.
                </p>
              </div>
            </div>

            <div className="p-5 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => setActiveField(null)}
                className="flex-1 px-4 py-2.5 border border-grey-300 text-grey-700 rounded-xl font-medium text-sm hover:bg-grey-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSignature}
                className="flex-1 px-4 py-2.5 bg-pebble-red text-white rounded-xl font-medium text-sm hover:bg-red-700 transition"
              >
                Apply {activeField.type === "initials" ? "Initials" : "Signature"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSigningModal;
